<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { Previewer } from 'pagedjs'
import api from '@/services/api'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import { useRelatorioTemplate } from '@/components/relatorios/useRelatorioTemplate'
import { imprimirRelatorio } from '@/services/relatorioPrintService'
import { extrairConfiguracaoPagina, calcularDimensoesPagina, gerarCssPage, gerarCssPrintPage } from '@/components/relatorios/useRelatorioImagens'

const props = defineProps({
  templates: { type: Array, default: () => [] },
  dadosRelatorio: { type: Object, default: null },
  visible: { type: Boolean, default: false },
  imagensConfig: { type: Object, default: () => ({}) },
  idEmpresa: { type: Number, default: null },
  // true apenas no cadastro de templates (EmpresasCadastro) — exibe o badge de preview.
  // Nos módulos reais (produção) permanece false, para o relatório sair sem o badge.
  modoPreview: { type: Boolean, default: false }
})

const emit = defineEmits(['fechar', 'show-notification'])

const { componenteAtual, isLoading, carregarComponente, obterDadosMock } = useRelatorioTemplate()

const templateAtivo = ref(0)
const templateRef = ref(null)
const dadosAtuais = ref(null)
const isGerando = ref(false)
const imagensResolvidas = ref({})
const configuracaoPagina = ref({ pageSize: 'A4', pageOrientation: 'portrait', pageMargins: [40, 60, 40, 60] })

// --- Paged.js ---
const pagedContainer = ref(null)
let previewer = null

// --- Zoom / viewport ---
const viewportRef = ref(null)
const dimensoesViewport = ref({ width: 0, height: 0 })
const modoZoom = ref('ajustar') // 'ajustar' | 'largura' | 'altura' | 'custom'
const zoomCustom = ref(1)
let roViewport = null

const templateSelecionado = computed(() => {
  return props.templates[templateAtivo.value] || null
})

// Rótulo do tamanho do papel (ex: "A4" ou "595×842" quando for array)
const rotuloTamanhoPagina = computed(() => {
  const ps = configuracaoPagina.value.pageSize
  if (Array.isArray(ps)) return `${ps[0]}×${ps[1]}`
  return ps
})

// Indica orientação paisagem (landscape)
const isPaisagem = computed(() => configuracaoPagina.value.pageOrientation === 'landscape')

// Dimensões naturais da página (em points)
const dimensoesPagina = computed(() => {
  const { pageSize, pageOrientation } = configuracaoPagina.value
  return calcularDimensoesPagina(pageSize, pageOrientation)
})

// Nº de páginas detectadas pelo Paged.js
const numPaginas = ref(1)
const paginaAtual = ref(1)

// Escala de UMA página para caber no viewport (zoom)
const escalaAplicada = computed(() => {
  const { width: vw, height: vh } = dimensoesViewport.value
  const { width: pw, height: ph } = dimensoesPagina.value
  if (!vw || !vh || !pw || !ph) return 1

  if (modoZoom.value === 'custom') return zoomCustom.value
  if (modoZoom.value === 'largura') return vw / pw
  if (modoZoom.value === 'altura') return vh / ph
  // 'ajustar' → contain (uma página)
  return Math.min(vw / pw, vh / ph)
})

// Zoom aplicado via transform scale no container do Paged.js
const estiloContainer = computed(() => ({
  transform: `scale(${escalaAplicada.value})`,
  transformOrigin: 'top center'
}))

const percentualZoom = computed(() => Math.round(escalaAplicada.value * 100))

const zoomIn = () => {
  const atual = escalaAplicada.value
  modoZoom.value = 'custom'
  zoomCustom.value = Math.min(atual + 0.1, 4)
}

const zoomOut = () => {
  const atual = escalaAplicada.value
  modoZoom.value = 'custom'
  zoomCustom.value = Math.max(atual - 0.1, 0.2)
}

// --- Navegação entre páginas ---
const irParaPagina = (p) => {
  paginaAtual.value = Math.min(Math.max(1, p), numPaginas.value)
  const paginaEl = document.querySelector(`.pagedjs_page:nth-child(${paginaAtual.value})`)
  paginaEl?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const paginaAnterior = () => irParaPagina(paginaAtual.value - 1)
const proximaPagina = () => irParaPagina(paginaAtual.value + 1)

// --- Medição do viewport ---
const medirViewport = () => {
  const el = viewportRef.value
  if (!el) return
  const estilo = getComputedStyle(el)
  const padX = parseFloat(estilo.paddingLeft) + parseFloat(estilo.paddingRight)
  const padY = parseFloat(estilo.paddingTop) + parseFloat(estilo.paddingBottom)
  const w = Math.max(0, el.clientWidth - padX)
  const h = Math.max(0, el.clientHeight - padY)
  // Evita loop de ResizeObserver: só atualiza se mudou de fato
  if (w === dimensoesViewport.value.width && h === dimensoesViewport.value.height) return
  dimensoesViewport.value = { width: w, height: h }
}

// --- Renderização com Paged.js ---

/**
 * Aguarda o container estar visível/renderizado.
 * O Paged.js usa element.offsetParent.getBoundingClientRect() (layout.js:43),
 * que é null quando o container ou um ancestral está display:none/detached
 * — causa do erro "Cannot read properties of null (reading 'getBoundingClientRect')".
 */
const aguardarContainerPronto = async (el, maxFrames = 40) => {
  for (let i = 0; i < maxFrames; i++) {
    const rect = el.getBoundingClientRect()
    if (el.isConnected && el.offsetParent !== null && rect.width > 0 && rect.height > 0) {
      return true
    }
    await new Promise((resolve) => requestAnimationFrame(resolve))
  }
  return false
}

// Token de geração: garante que apenas o render mais recente seja aplicado
let renderToken = 0
// Promise do render em andamento (serializa chamadas concorrentes)
let renderOngoing = null

const renderizarComPagedJs = async () => {
  const meuToken = ++renderToken

  const container = pagedContainer.value
  if (!container) return

  // Serializa encadeando na promise anterior. CRÍTICO: `renderOngoing` deve ser
  // atribuído SÍNCRONAMENTE (antes de qualquer await). Se fosse atribuído após
  // um await intermediário (ex.: aguardarContainerPronto), as chamadas
  // disparadas em sequência (carregarTemplateAtivo + watchers) passariam todas
  // pelo `if (renderOngoing)` enquanto ele ainda é null, e cada uma limparia e
  // anexaria um novo .pagedjs_pages → páginas duplicadas (3x).
  const anterior = renderOngoing
  const minhaPromise = (async () => {
    if (anterior) {
      try { await anterior } catch {}
    }
    // Se um render mais novo já foi solicitado, este é obsoleto
    if (meuToken !== renderToken) return

    // Limpar renderização anterior
    container.innerHTML = ''
    try { previewer?.destroy?.() } catch {}

    // Garantir visibilidade do container antes de rodar o Paged.js
    const pronto = await aguardarContainerPronto(container)
    if (!pronto) {
      console.warn('[RelatorioPreview] Container não visível a tempo:', {
        isConnected: container.isConnected,
        offsetParent: container.offsetParent?.className?.toString?.() ?? null,
        display: getComputedStyle(container).display,
        rect: container.getBoundingClientRect(),
        ancestrais: (() => {
          const arr = []
          let cur = container
          while (cur && cur !== document.documentElement) {
            const cs = getComputedStyle(cur)
            arr.push(`${cur.tagName}.${cur.className?.toString?.().slice(0, 40)} [display:${cs.display}; position:${cs.position}; transform:${cs.transform}]`)
            cur = cur.parentElement
          }
          return arr
        })()
      })
    }

    try {
      previewer = new Previewer()

      // Corrige um bug do Paged.js 0.4.x: o reflow pós-render
      // (checkUnderflowAfterResize/checkOverflowAfterResize) chama
      // findEndToken → nodeAfter(null) → "Cannot read properties of null
      // (reading 'nextSibling')" durante zoom/resize. Substituímos esses
      // métodos por no-ops em cada página criada pelo chunker. As páginas já
      // estão corretamente fragmentadas no render inicial.
      const chunker = previewer.chunker
      // Aplica no-op nos métodos de reflow de uma página
      const desabilitarReflow = (page) => {
        if (!page) return page
        page.checkUnderflowAfterResize = () => {}
        page.checkOverflowAfterResize = () => {}
        return page
      }
      if (chunker) {
        if (typeof chunker.addPage === 'function') {
          const origAddPage = chunker.addPage.bind(chunker)
          chunker.addPage = (blank) => desabilitarReflow(origAddPage(blank))
        }
        if (typeof chunker.insertPage === 'function') {
          const origInsertPage = chunker.insertPage.bind(chunker)
          chunker.insertPage = (index, blank) => desabilitarReflow(origInsertPage(index, blank))
        }
        if (typeof chunker.clonePage === 'function') {
          const origClonePage = chunker.clonePage.bind(chunker)
          chunker.clonePage = (originalPage) => desabilitarReflow(origClonePage(originalPage))
        }
      }

      // Clonar o conteúdo do template para o container
      const templateEl = templateRef.value?.$el
      if (!templateEl) return
      const content = templateEl.cloneNode(true)

      // Remover nós de comentário do Vue (v-if/v-for/v-show) e texto vazio
      limparNodosVue(content)

      // Passa o @page dinâmico (size/margin) ao Paged.js via Blob URL no
      // parâmetro `stylesheets`. Injetar <style> no head não é confiável: o
      // Polisher nem sempre coleta a regra a tempo, ignorando orientação/margens.
      const cssPage = gerarCssPage(configuracaoPagina.value)
      const cssBlob = new Blob([cssPage], { type: 'text/css' })
      const cssUrl = URL.createObjectURL(cssBlob)

      // Injeta o @page de IMPRESSÃO (size dinâmico + margin 0) usado pelo
      // window.print. As folhas do Paged.js já contêm as margens internamente,
      // então a margem do PAPEL deve ser 0 — senão as folhas deslocam/transbordam.
      const printStyleId = 'rp-print-page-style'
      document.getElementById(printStyleId)?.remove()
      const printStyle = document.createElement('style')
      printStyle.id = printStyleId
      printStyle.textContent = gerarCssPrintPage(configuracaoPagina.value)
      document.head.appendChild(printStyle)

      // Renderizar via Paged.js
      let result
      try {
        result = await previewer.preview(content, [cssUrl], container)
      } finally {
        URL.revokeObjectURL(cssUrl)
      }

      // Se um render mais novo começou durante o preview, descartar este resultado
      if (meuToken !== renderToken) return

      // Diagnóstico: múltiplas áreas .pagedjs_pages indicam renders acumulando DOM
      const areas = container.querySelectorAll(':scope > .pagedjs_pages')
      const domPages = container.querySelectorAll('.pagedjs_page')
      console.log(`[RelatorioPreview] token=${meuToken} total=${result.total} areas=${areas.length} domPages=${domPages.length}`)
      // Remove áreas de páginas duplicadas, mantendo apenas a última (a atual)
      if (areas.length > 1) {
        for (let i = 0; i < areas.length - 1; i++) areas[i].remove()
      }

      numPaginas.value = result.total
      paginaAtual.value = 1
    } catch (err) {
      if (meuToken === renderToken) {
        console.error('Erro no Paged.js preview:', err)
        emit('show-notification', { message: 'Erro ao renderizar preview do relatório.', type: 'error' })
      }
    }
  })()

  renderOngoing = minhaPromise
  try {
    await minhaPromise
  } finally {
    if (renderOngoing === minhaPromise) renderOngoing = null
  }
}

/**
 * Remove nós de comentário do Vue (<!-- -->) e nós de texto vazio
 * que o Paged.js não consegue processar (nextSignificantNode retorna null).
 * Percorre recursivamente a árvore DOM do conteúdo clonado.
 */
const limparNodosVue = (node) => {
  let child = node.firstChild
  while (child) {
    const next = child.nextSibling
    if (child.nodeType === 8 || // nó de comentário (v-if/v-for/v-show)
        (child.nodeType === 3 && !child.nodeValue?.trim())) { // texto com apenas whitespace
      child.remove()
    } else if (child.nodeType === 1) { // elemento — recursão
      limparNodosVue(child)
    }
    child = next
  }
}

// --- Imagens ---
const obterConfigImagens = () => {
  if (Object.keys(props.imagensConfig).length > 0) return props.imagensConfig
  const tmpl = templateSelecionado.value
  if (tmpl?.imagensConfig && Object.keys(tmpl.imagensConfig).length > 0) return tmpl.imagensConfig
  return {}
}

const obterDimensoesImagem = (dataUrl) => {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ largura: img.naturalWidth, altura: img.naturalHeight })
    img.onerror = () => resolve(null)
    img.src = dataUrl
  })
}

const baixarImagemComoBase64 = async (idImagem) => {
  try {
    const response = await api.get(`/EmpresaImagens/${idImagem}/arquivo`, {
      responseType: 'arraybuffer',
      _suppressLoading: true
    })
    const contentType = response.headers?.['content-type'] || 'image/png'
    const blob = new Blob([response.data], { type: contentType })
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (e) {
    console.warn(`Erro ao baixar imagem ${idImagem}:`, e.message)
    return null
  }
}

const obterLimitesSlots = () => {
  const tmpl = templateSelecionado.value
  if (!tmpl?.slotsImagemJson) return {}
  try {
    const slots = JSON.parse(tmpl.slotsImagemJson)
    const limites = {}
    for (const slot of slots) {
      if (slot.chave && (slot.maxWidth || slot.maxHeight)) {
        limites[slot.chave] = { maxWidth: slot.maxWidth, maxHeight: slot.maxHeight }
      }
    }
    return limites
  } catch (e) {
    return {}
  }
}

const resolverImagens = async () => {
  const config = obterConfigImagens()
  const limitesSlots = obterLimitesSlots()
  const ids = Object.values(config).filter(id => id != null)
  if (ids.length === 0 || !props.idEmpresa) {
    imagensResolvidas.value = {}
    return
  }
  const resolved = {}
  for (const [chave, idImagem] of Object.entries(config)) {
    if (idImagem != null) {
      const dataUrl = await baixarImagemComoBase64(idImagem)
      if (dataUrl) {
        const dims = await obterDimensoesImagem(dataUrl)
        resolved[chave] = {
          dataUrl,
          larguraOriginal: dims?.largura || null,
          alturaOriginal: dims?.altura || null,
          maxWidth: limitesSlots[chave]?.maxWidth || null,
          maxHeight: limitesSlots[chave]?.maxHeight || null
        }
      } else {
        resolved[chave] = null
      }
    } else {
      resolved[chave] = null
    }
  }
  imagensResolvidas.value = resolved
}

// --- Impressão ---

/**
 * Garante que o bloco @page de impressão (size dinâmico + margin 0) seja o
 * ÚLTIMO <style> do <head>. O Paged.js injeta o próprio @page (com as margens
 * do relatório) durante o preview; se ele vier depois do nosso, a margem do
 * papel volta a ser aplicada e as folhas transbordam à direita/inferior.
 */
const garantirPrintPageStyle = () => {
  const id = 'rp-print-page-style'
  document.getElementById(id)?.remove()
  const style = document.createElement('style')
  style.id = id
  style.textContent = gerarCssPrintPage(configuracaoPagina.value)
  document.head.appendChild(style)
}

/**
 * Prepara a impressão clonando .pagedjs_pages para um #rp-print-root que é
 * filho DIRETO do <body>. O clone preserva os estilos inline aplicados pelo
 * Paged.js, mas fica fora da árvore do modal — sem ancestrais com display:none,
 * overflow ou transform que impediriam a renderização. O CSS (@media print)
 * esconde todo o resto e exibe apenas #rp-print-root.
 *
 * @returns {() => void} função de limpeza
 */
const prepararImpressao = () => {
  const pages = document.querySelector('.pagedjs_pages')
  if (!pages) {
    console.warn('[RelatorioPreview] .pagedjs_pages não encontrado para impressão.')
    return () => {}
  }

  document.getElementById('rp-print-root')?.remove()

  const root = document.createElement('div')
  root.id = 'rp-print-root'
  root.appendChild(pages.cloneNode(true))
  document.body.appendChild(root)

  garantirPrintPageStyle()

  const limpar = () => {
    document.getElementById('rp-print-root')?.remove()
  }
  window.addEventListener('afterprint', limpar, { once: true })
  return limpar
}

// --- Ações ---
const gerarPDFHandler = async () => {
  if (isGerando.value) return
  isGerando.value = true
  try {
    // Garantir que o Paged.js terminou a renderização
    await renderizarComPagedJs()
    await nextTick()
    // Definir título da página para o nome do arquivo no diálogo de impressão
    const tmpl = templateSelecionado.value
    document.title = `relatorio_${tmpl?.nome?.replace(/\s+/g, '_') || 'documento'}`
    // Clonar as folhas para o root de impressão e disparar a impressão nativa
    const limparImpressao = prepararImpressao()
    try {
      imprimirRelatorio()
    } finally {
      // Fallback caso o evento `afterprint` não dispare
      setTimeout(limparImpressao, 2000)
    }
    emit('show-notification', { message: 'Impressão enviada.', type: 'success' })
  } catch (error) {
    console.error('Erro ao imprimir:', error)
    emit('show-notification', { message: 'Erro ao gerar PDF.', type: 'error' })
  } finally {
    isGerando.value = false
  }
}

const fechar = () => {
  emit('fechar')
}

const carregarTemplateAtivo = async () => {
  const tmpl = templateSelecionado.value
  if (!tmpl) return

  await carregarComponente(tmpl.caminhoComponente)

  if (!props.dadosRelatorio) {
    dadosAtuais.value = await obterDadosMock(tmpl.caminhoComponente)
  } else {
    dadosAtuais.value = props.dadosRelatorio
  }

  await resolverImagens()
  configuracaoPagina.value = extrairConfiguracaoPagina(tmpl.configuracaoJson)
  modoZoom.value = 'ajustar'
  paginaAtual.value = 1

  await nextTick()
  await new Promise(resolve => requestAnimationFrame(resolve))
  medirViewport()
  await renderizarComPagedJs()
}

// --- Observadores ---
watch(() => props.visible, async (novoValor) => {
  if (novoValor) {
    templateAtivo.value = 0
    await nextTick()
    await carregarTemplateAtivo()
    if (viewportRef.value) {
      roViewport = new ResizeObserver(medirViewport)
      roViewport.observe(viewportRef.value)
    }
  } else {
    roViewport?.disconnect()
    roViewport = null
  }
})

watch(templateAtivo, async () => {
  await nextTick()
  await carregarTemplateAtivo()
})

// Re-renderiza com Paged.js quando dados ou configuração mudam
watch(() => dadosAtuais.value, async () => {
  await nextTick()
  await new Promise(resolve => requestAnimationFrame(resolve))
  await renderizarComPagedJs()
})

watch(() => [configuracaoPagina.value.pageOrientation, configuracaoPagina.value.pageSize], async () => {
  await nextTick()
  await new Promise(resolve => requestAnimationFrame(resolve))
  await renderizarComPagedJs()
})

onUnmounted(() => {
  roViewport?.disconnect()
  try { previewer?.destroy?.() } catch {}
})
</script>

<template>
  <ModalFormularioV2
    :model-value="visible"
    :titulo="'Relatório'"
    tamanho="full"
    @update:model-value="fechar"
  >
    <template #footer>
      <div class="d-flex justify-content-between align-items-center gap-2 w-100">
        <!-- Controles de zoom à esquerda -->
        <div class="d-flex align-items-center gap-1">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="zoomOut"
            title="Reduzir zoom"
          >
            <i class="bi bi-dash-lg"></i>
          </button>
          <span
            class="badge bg-light text-dark px-2 py-1"
            style="min-width:48px;font-size:12px;cursor:default"
          >
            {{ percentualZoom }}%
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="zoomIn"
            title="Aumentar zoom"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
          <div class="vr mx-1"></div>
          <button
            type="button"
            class="btn btn-sm btn-fit"
            :class="modoZoom==='ajustar'?'btn-info text-white':'btn-outline-secondary'"
            @click="modoZoom='ajustar'"
            title="Ajustar à tela (contain)"
          >
            Tela
          </button>
          <button
            type="button"
            class="btn btn-sm btn-fit"
            :class="modoZoom==='largura'?'btn-info text-white':'btn-outline-secondary'"
            @click="modoZoom='largura'"
            title="Ajustar à largura"
          >
            Largura
          </button>
          <button
            type="button"
            class="btn btn-sm btn-fit"
            :class="modoZoom==='altura'?'btn-info text-white':'btn-outline-secondary'"
            @click="modoZoom='altura'"
            title="Ajustar à altura"
          >
            Altura
          </button>
          <div class="vr mx-1"></div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="paginaAtual <= 1"
            @click="paginaAnterior"
            title="Página anterior"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <span class="badge bg-secondary-subtle text-secondary-emphasis">
            Página {{ paginaAtual }} de {{ numPaginas }}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="paginaAtual >= numPaginas"
            @click="proximaPagina"
            title="Próxima página"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
        <!-- Botões de ação à direita -->
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="fechar">
            <i class="bi bi-x-lg me-1"></i> Fechar
          </button>
          <button
            type="button"
            class="btn btn-info text-white fw-bold"
            :disabled="isGerando || isLoading || !componenteAtual"
            @click="gerarPDFHandler"
          >
            <span v-if="isGerando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-pdf me-1"></i>
            {{ isGerando ? 'Gerando...' : 'Gerar PDF' }}
          </button>
        </div>
      </div>
    </template>

    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">Carregando template...</span>
      </div>
      <p class="text-muted">Carregando template de relatório...</p>
    </div>

    <!-- Sem templates -->
    <div v-else-if="!templates || templates.length === 0" class="text-center py-5">
      <i class="bi bi-file-earmark-pdf fs-1 text-muted d-block mb-3"></i>
      <p class="text-muted">Nenhum template de relatório disponível.</p>
    </div>

    <!-- Com templates -->
    <template v-else>
      <!-- Cabeçalho: seletor de template (esquerda) + configuração de página (direita) -->
      <div class="d-flex justify-content-between align-items-center mb-3 gap-3">
        <!-- Seletor: abas (2+) ou nome do template único -->
        <div class="flex-grow-1 rp-header-left">
          <ul v-if="templates.length > 1" class="nav nav-tabs rp-tabs">
            <li
              v-for="(tmpl, idx) in templates"
              :key="tmpl.idRelatorioTemplate || idx"
              class="nav-item"
            >
              <a
                class="nav-link"
                :class="{ active: templateAtivo === idx }"
                @click="templateAtivo = idx"
              >
                {{ tmpl.nome }}
              </a>
            </li>
          </ul>
          <div v-else-if="templateSelecionado">
            <h5 class="mb-1">{{ templateSelecionado.nome }}</h5>
            <p v-if="templateSelecionado.descricao" class="text-muted small mb-0">
              {{ templateSelecionado.descricao }}
            </p>
          </div>
        </div>

        <!-- Configuração de página (ConfiguracaoJson) — indicador visual compacto -->
        <div
          v-if="templateSelecionado"
          class="rp-cfg d-flex align-items-center gap-3 flex-shrink-0"
        >
          <!-- Tamanho do papel: folha com o rótulo (A4) dentro -->
          <div class="rp-cfg-item" :title="'Tamanho da página: ' + rotuloTamanhoPagina">
            <div class="rp-paper">
              <span class="rp-paper-label">{{ rotuloTamanhoPagina }}</span>
            </div>
          </div>

          <!-- Orientação: folha vertical (retrato) ou horizontal (paisagem) + rótulo -->
          <div
            class="rp-cfg-item"
            :title="'Orientação: ' + (isPaisagem ? 'Paisagem' : 'Retrato')"
          >
            <div
              class="rp-orient"
              :class="isPaisagem ? 'rp-orient--landscape' : 'rp-orient--portrait'"
            ></div>
            <span class="rp-cfg-caption">{{ isPaisagem ? 'Paisagem' : 'Retrato' }}</span>
          </div>

          <!-- Margens: números posicionados ao redor da folha -->
          <div
            class="rp-cfg-item"
            title="Margens da página (topo, direita, baixo, esquerda)"
          >
            <div class="rp-margins">
              <span class="rp-mg rp-mg-top">{{ configuracaoPagina.pageMargins[0] }}</span>
              <span class="rp-mg rp-mg-left">{{ configuracaoPagina.pageMargins[3] }}</span>
              <div class="rp-mg-page"></div>
              <span class="rp-mg rp-mg-right">{{ configuracaoPagina.pageMargins[1] }}</span>
              <span class="rp-mg rp-mg-bottom">{{ configuracaoPagina.pageMargins[2] }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Viewport + Páginas renderizadas pelo Paged.js -->
      <div
        v-if="componenteAtual && dadosAtuais"
        ref="viewportRef"
        class="relatorio-viewport"
      >
        <!-- O transform de zoom fica num wrapper FORA do container do Paged.js,
             para não interferir no offsetParent dos elementos internos -->
        <div class="relatorio-zoom" :style="estiloContainer">
          <div ref="pagedContainer" class="pagedjs-container">
            <!-- Paged.js insere .pagedjs_pages aqui -->
          </div>
        </div>
      </div>

      <!-- Template fonte (invisível) para o Paged.js processar -->
      <div v-if="componenteAtual && dadosAtuais" class="rp-source">
        <component
          :is="componenteAtual"
          :dados="dadosAtuais"
          :imagens="imagensResolvidas"
          :is-preview="modoPreview"
          :page-orientation="configuracaoPagina.pageOrientation"
          :page-size="configuracaoPagina.pageSize"
          ref="templateRef"
        />
      </div>
    </template>
  </ModalFormularioV2>
</template>

<style scoped>
.relatorio-viewport {
  height: calc(95vh - 240px);
  min-height: 280px;
  overflow: auto;
  scrollbar-gutter: stable;
  background: var(--bs-secondary-bg);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px;
}

/* Wrapper do zoom — recebe o transform scale, FORA do container do Paged.js,
   para não interferir no offsetParent dos elementos internos do Paged.js */
.relatorio-zoom {
  transform-origin: top center;
}

/* Container onde o Paged.js insere as páginas fragmentadas */
.pagedjs-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

/* Sombra simulando página impressa */
.pagedjs-container :deep(.pagedjs_page) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
  background: #fff;
}

/* Template fonte (invisível) — Paged.js clona este conteúdo */
.rp-source {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 595px; /* largura A4 em points — fallback */
  pointer-events: none;
  opacity: 0;
}

.rp-header-left {
  min-width: 0;
}

.rp-tabs {
  margin-bottom: 0;
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.btn-fit {
  min-width: 72px;
}

/* ============================================================ */
/* Indicador visual da configuração de página (ConfiguracaoJson) */
/* Metáfora de folha — convenção universal de impressão         */
/* ============================================================ */
.rp-cfg {
  color: var(--bs-secondary-color);
  user-select: none;
}

.rp-cfg-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.rp-cfg-caption {
  font-size: 0.6rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

/* Tamanho do papel: folha com o rótulo dentro */
.rp-paper {
  width: 24px;
  height: 30px;
  border: 1.5px solid var(--bs-secondary-color);
  border-radius: 3px;
  background: var(--bs-body-bg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.rp-paper-label {
  font-size: 0.55rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.2px;
}

/* Orientação: folha vertical (retrato) ou horizontal (paisagem) */
.rp-orient {
  position: relative;
  border: 1.5px solid var(--bs-secondary-color);
  border-radius: 3px;
  background: var(--bs-body-bg);
  overflow: hidden;
}

.rp-orient--portrait {
  width: 22px;
  height: 30px;
}

.rp-orient--landscape {
  width: 30px;
  height: 22px;
}

/* Linhas internas sugerindo o sentido do texto na folha */
.rp-orient::after {
  content: '';
  position: absolute;
  inset: 4px 3px;
  background-image: repeating-linear-gradient(
    to bottom,
    var(--bs-secondary-color) 0,
    var(--bs-secondary-color) 1px,
    transparent 1px,
    transparent 4px
  );
  opacity: 0.5;
}

/* Margens: números dispostos ao redor da folha */
.rp-margins {
  display: grid;
  grid-template-columns: 16px 26px 16px;
  grid-template-rows: 12px 30px 12px;
  align-items: center;
  justify-items: center;
}

.rp-mg {
  font-size: 0.55rem;
  font-weight: 600;
  line-height: 1;
  color: var(--bs-secondary-color);
}

.rp-mg-top { grid-area: 1 / 2; }
.rp-mg-left { grid-area: 2 / 1; }
.rp-mg-right { grid-area: 2 / 3; }
.rp-mg-bottom { grid-area: 3 / 2; }

.rp-mg-page {
  grid-area: 2 / 2;
  width: 24px;
  height: 28px;
  border: 1.5px solid var(--bs-secondary-color);
  border-radius: 3px;
  background: var(--bs-body-bg);
  position: relative;
}

.rp-mg-page::after {
  content: '';
  position: absolute;
  inset: 4px;
  border: 1px dashed var(--bs-secondary-color);
  border-radius: 2px;
  opacity: 0.7;
}
</style>
